/** The words of the sandbox surfaces (SBX plan D3–D8; dev-platform metaplan S5b) — one place, so the
 *  operator page's banners, its Sandbox tab and the create wizard say the same thing. */
import type {
  BreakerAction,
  IngressMode,
  OutboundMode,
  PullPausedReason,
  QuotaKey,
  SandboxAdminMembership,
  ScheduledPull,
  SandboxBreaker,
  SandboxSuspension,
  SandboxThrottle,
} from '@/api/sandbox';

export const OUTBOUND_MODE_LABEL: Record<OutboundMode, string> = {
  outbox: 'Outbox only',
  allowlist: 'Allowlist',
  allow_all: 'Allow all',
};

/** What each mode does with the sandbox's sends (SBX D5 "Modes"). */
export const OUTBOUND_MODE_HELP: Record<OutboundMode, string> = {
  outbox: 'Nothing is delivered: every send is rendered and kept in the sandbox’s outbox.',
  allowlist: 'Only allowlisted destinations get the send; everything else is kept in the outbox.',
  allow_all:
    'Everything is delivered (and recorded in the outbox). Only for vendor accounts whose bookings carry test guests.',
};

export const OUTBOUND_MODES: readonly OutboundMode[] = ['outbox', 'allowlist', 'allow_all'];

export const QUOTA_LABEL: Record<QuotaKey, string> = {
  bookings_per_day: 'Bookings a day',
  bookings_total: 'Bookings in total',
  bookings_per_payload: 'Bookings per payload',
  requests_per_minute: 'Requests a minute',
};

export const BREAKER_ACTION_LABEL: Record<BreakerAction, string> = {
  deliver: 'Deliver the held requests',
  drop: 'Drop the held requests',
};

export const INGRESS_MODE_LABEL: Record<IngressMode, string> = {
  log: 'Log only',
  enforce: 'Enforced',
};

/** Why a request did not count as coming through the sandbox ingress host (S6a's guard). */
export const INGRESS_REASON_LABEL: Record<string, string> = {
  no_secret: 'did not come through bproxy',
  production_policy: 'came through the production host',
  no_marker: 'carried no sandbox policy',
};

const pct = (ratio: number | undefined) => `${Math.round((ratio ?? 0) * 100)} %`;

/** "≥ 20 % of the admitted requests fail, or ≥ 50 % are answered 429, over 15 minutes (from 20 requests)". */
export function breakerThresholdsText(b: Pick<SandboxBreaker, 'thresholds'>): string {
  const t = b.thresholds;
  return (
    `Trips when, over ${t.window_minutes ?? 15} minutes, ${pct(t.failure_ratio)} or more of the admitted ` +
    `requests fail (5xx or rejected), or ${pct(t.rate_limited_ratio)} or more are answered 429 ` +
    `— from ${t.min_requests ?? 20} requests on.`
  );
}

/** Why a tripped breaker tripped, with the numbers it rests on. */
export function breakerReasonText(b: SandboxBreaker): string {
  const s = b.stats;
  if (b.reason === 'failures') {
    return s
      ? `${s.failed ?? 0} of ${s.admitted ?? 0} admitted requests failed (${pct(s.failure_ratio)}; threshold ${pct(b.thresholds.failure_ratio)})`
      : 'Too many admitted requests failed';
  }
  if (b.reason === 'rate_limited') {
    return s
      ? `${s.rate_limited ?? 0} of ${s.answered ?? 0} requests were answered 429 (${pct(s.rate_limited_ratio)}; threshold ${pct(b.thresholds.rate_limited_ratio)})`
      : 'Too many requests were answered 429';
  }
  return 'Tripped';
}

/** "Suspended by ops" / "Suspended by its developers" — `partner` is the sandbox's own admins. */
export function suspendedByText(s: SandboxSuspension): string {
  return s.suspended_by === 'partner' ? 'Suspended by its developers' : 'Suspended by ops';
}

/** The lane-paused line: which quota, how far, and until when. */
export function throttleText(t: SandboxThrottle): string {
  const quota = t.quota ? (QUOTA_LABEL[t.quota as QuotaKey] ?? t.quota) : 'A quota';
  const reached = `${quota}: ${t.count ?? '?'} of ${t.limit ?? '?'}`;
  return t.reset_at
    ? `${reached} — paused until the daily reset (00:00 UTC)`
    : `${reached} — paused until the quota is raised or the sandbox is reset`;
}

/** The scheduled pull's chip while the sandbox's lane is paused (S6c `paused_reason`). */
export const PULL_PAUSED_LABEL: Record<PullPausedReason, string> = {
  // ESB: sandboxes pull nothing while `SANDBOX_PULL["enabled"]` is off (the default).
  pulls_off: 'Off — sandboxes don’t pull',
  quota_paused: 'Paused — booking quota reached',
  breaker_paused: 'Paused — breaker tripped',
  state_unavailable: 'Paused — state unreadable',
};

/** Per restech account: whether its next scheduled pull runs (S6c `restechs[].scheduled_pull`). */
export const SCHEDULED_PULL_LABEL: Record<ScheduledPull, string> = {
  allowed: 'runs',
  pulls_off: 'off — sandboxes don’t pull',
  quota_paused: 'skipped — lane paused',
  breaker_paused: 'skipped — lane paused',
  state_unavailable: 'skipped — its pull state cannot be read',
  no_priority_class: 'no scheduled pull — its shared limiter has no sandbox priority class',
  vendor_backoff: 'backing off',
};

/** The scheduled pull kinds, in the order of the backend's cadence defaults (`pull.DEFAULTS`). */
const PULL_KIND_LABEL: Record<string, string> = {
  rectify: 'rectify',
  ventrata_reconcile: 'Ventrata reconcile',
  juniper_poll: 'Juniper poll',
  products: 'product update',
  fetch_imports: 'scheduled imports',
};

/** "rectify at most every 59 min, …, product update every cron tick" — a floor between two scheduled
 *  pulls (`min_interval_minutes`), 0 meaning every cron tick; kinds the console does not know are
 *  listed last, by their key. Empty when there is no cadence. */
export function pullCadenceText(minutes: Record<string, number> | null | undefined): string {
  if (!minutes) return '';
  const known = Object.keys(PULL_KIND_LABEL).filter((k) => k in minutes);
  const other = Object.keys(minutes).filter((k) => !(k in PULL_KIND_LABEL));
  return [...known, ...other]
    .map((k) => {
      const n = minutes[k] ?? 0;
      return `${PULL_KIND_LABEL[k] ?? k} ${n > 0 ? `at most every ${n} min` : 'every cron tick'}`;
    })
    .join(', ');
}

/** How a sandbox admin (DSEC D6) holds its admin rights: a full-access group, an `operator_admin_*` group,
 *  or recorded at creation and still waiting for that group (ACCESS_GROUPS_SETUP). */
export const ADMIN_MEMBERSHIP_LABEL: Record<SandboxAdminMembership, string> = {
  full_access: 'Full access',
  admin_group: 'Admin group',
  pending: 'Pending: waiting for its admin group',
};
